import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCopy, FaEnvelope, FaLink, FaUserCheck, FaExternalLinkAlt } from 'react-icons/fa';
import workspaceAPI from '../../services/workspaceAPI';

const INVITE_VALIDITY_OPTIONS = [
  { value: '30m', label: '30 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '12h', label: '12 hours' },
  { value: '1d', label: '1 day' },
  { value: '3d', label: '3 days' },
  { value: '7d', label: '7 days' },
  { value: '15d', label: '15 days' },
  { value: '30d', label: '30 days' },
];

/**
 * Embedded staff management panel for company admin dashboard.
 */
const WorkspaceStaffPanel = ({ companyInviteCode: initialCode }) => {
  const [staff, setStaff] = useState([]);
  const [inviteCode, setInviteCode] = useState(initialCode || '');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('company_staff');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inviteCodeValidTill, setInviteCodeValidTill] = useState('');
  const [showValidityOptions, setShowValidityOptions] = useState(false);
  const [validityMode, setValidityMode] = useState('preset');
  const [validityPreset, setValidityPreset] = useState('7d');
  const [validityCustomUntil, setValidityCustomUntil] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [staffRes, codeRes, linkRes] = await Promise.all([
        workspaceAPI.getStaff(),
        workspaceAPI.getInviteCode().catch(() => ({ data: {} })),
        workspaceAPI.getStaffInviteLink(),
      ]);
      setStaff(staffRes.data.staff || []);
      if (codeRes.data?.inviteCode) setInviteCode(codeRes.data.inviteCode);
      setInviteCodeValidTill(codeRes.data?.validTill || '');
      setInviteLink(linkRes.data.inviteUrl || '');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (initialCode) setInviteCode(initialCode);
  }, [initialCode]);

  const copy = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setMessage(`${label} copied to clipboard`);
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setError('Could not copy');
    }
  };

  const sendInviteLink = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      setError('Enter staff email address');
      return;
    }
    setSending(true);
    setError('');
    setMessage('');
    try {
      const res = await workspaceAPI.sendStaffInviteLink({
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setMessage(res.data.message || 'Invite sent');
      if (res.data.inviteUrl) setInviteLink(res.data.inviteUrl);
      setInviteEmail('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send invite link');
    } finally {
      setSending(false);
    }
  };

  const pendingCount = staff.filter(
    (m) => m.role !== 'company_admin' && m.isActive && !m.loginReady
  ).length;
  const activeCount = staff.filter((m) => m.isActive && m.loginReady).length;

  if (loading) {
    return <div className="workspace-staff-panel workspace-card">Loading staff management...</div>;
  }

  return (
    <section className="workspace-staff-panel">
      <div className="workspace-staff-panel-header">
        <div>
          <h2 className="workspace-staff-panel-title">
            <FaUserCheck /> Staff management & authorized login
          </h2>
          <p className="workspace-page-subtitle" style={{ margin: 0 }}>
            Invite team members, send login links, and control who can access your workspace.
          </p>
        </div>
        <Link to="/workspace/staff" className="workspace-btn secondary" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
          Full staff page <FaExternalLinkAlt />
        </Link>
      </div>

      {error && <p className="workspace-alert error">{error}</p>}
      {message && <p className="workspace-alert success">{message}</p>}

      <div className="workspace-staff-stats-row">
        <span className="workspace-mini-stat">
          <strong>{staff.length}</strong> total
        </span>
        <span className="workspace-mini-stat ok">
          <strong>{activeCount}</strong> can log in
        </span>
        <span className="workspace-mini-stat pending">
          <strong>{pendingCount}</strong> pending setup
        </span>
      </div>

      <div className="workspace-staff-panel-grid">
        <div className="workspace-card workspace-staff-panel-card">
          <h3>Unified invite code</h3>
          <p className="workspace-hint">Staff enter this on join page or company login → Join with code</p>
          <div className="workspace-invite-code">{inviteCode || '—'}</div>
          <p className="workspace-hint workspace-invite-validity-note">
            {inviteCodeValidTill
              ? `Code valid till: ${new Date(inviteCodeValidTill).toLocaleString()}`
              : 'Code validity: no expiry'}
          </p>
          <button
            type="button"
            className="workspace-btn secondary workspace-validity-toggle"
            onClick={() => setShowValidityOptions((prev) => !prev)}
          >
            Code valid till
          </button>
          {showValidityOptions && (
            <div className="workspace-invite-validity-config">
              <label className="workspace-validity-mode">
                <input
                  type="radio"
                  name="invite-validity-mode-panel"
                  value="preset"
                  checked={validityMode === 'preset'}
                  onChange={() => setValidityMode('preset')}
                />
                Preset duration
              </label>
              <label className="workspace-validity-mode">
                <input
                  type="radio"
                  name="invite-validity-mode-panel"
                  value="custom"
                  checked={validityMode === 'custom'}
                  onChange={() => setValidityMode('custom')}
                />
                Custom date & time
              </label>
              <label className="workspace-validity-mode">
                <input
                  type="radio"
                  name="invite-validity-mode-panel"
                  value="never"
                  checked={validityMode === 'never'}
                  onChange={() => setValidityMode('never')}
                />
                No expiry
              </label>
              {validityMode === 'preset' && (
                <select
                  className="workspace-input"
                  value={validityPreset}
                  onChange={(e) => setValidityPreset(e.target.value)}
                >
                  {INVITE_VALIDITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              {validityMode === 'custom' && (
                <input
                  type="datetime-local"
                  className="workspace-input"
                  value={validityCustomUntil}
                  onChange={(e) => setValidityCustomUntil(e.target.value)}
                />
              )}
            </div>
          )}
          <div className="workspace-btn-row">
            <button type="button" className="workspace-btn secondary" onClick={() => copy(inviteCode, 'Code')}>
              <FaCopy /> Copy code
            </button>
            <button
              type="button"
              className="workspace-btn secondary"
              onClick={async () => {
                try {
                  const payload = {
                    validity: !showValidityOptions
                      ? { mode: 'never' }
                      : validityMode === 'preset'
                        ? { mode: 'preset', preset: validityPreset }
                        : validityMode === 'custom'
                          ? { mode: 'custom', customUntil: validityCustomUntil }
                          : { mode: 'never' },
                  };
                  const r = await workspaceAPI.regenerateInviteCode(payload);
                  setInviteCode(r.data.inviteCode);
                  setInviteCodeValidTill(r.data.validTill || '');
                  const linkRes = await workspaceAPI.getStaffInviteLink();
                  setInviteLink(linkRes.data.inviteUrl);
                  setMessage('New invite code generated');
                } catch (err) {
                  setError(err.response?.data?.message || 'Failed to regenerate');
                }
              }}
            >
              Regenerate
            </button>
          </div>
        </div>

        <div className="workspace-card workspace-staff-panel-card">
          <h3>Shareable invite link</h3>
          <p className="workspace-hint">Opens join page with code pre-filled</p>
          <input className="workspace-input workspace-link-input" readOnly value={inviteLink} />
          <button type="button" className="workspace-btn secondary" onClick={() => copy(inviteLink, 'Invite link')}>
            <FaLink /> Copy link
          </button>
        </div>
      </div>

      <form onSubmit={sendInviteLink} className="workspace-card workspace-send-invite-card">
        <h3>
          <FaEnvelope /> Send invite link by email
        </h3>
        <p className="workspace-hint">
          Authorizes the email and emails a personal link to set password and join workspace.
        </p>
        <div className="workspace-form-row">
          <input
            type="email"
            className="workspace-input"
            placeholder="staff@yourcompany.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <select
            className="workspace-input"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
          >
            <option value="company_staff">Staff</option>
            <option value="company_manager">Manager</option>
          </select>
          <button type="submit" className="workspace-btn" disabled={sending}>
            {sending ? 'Sending...' : 'Send invite link'}
          </button>
        </div>
      </form>

      <div className="workspace-card workspace-login-steps">
        <h3>Authorized staff login setup</h3>
        <ol className="workspace-steps-list">
          <li>Admin sends invite link or shares workspace code</li>
          <li>Staff opens link → sets password on <code>/join-workspace</code></li>
          <li>Or: Company login → <strong>Join with code</strong> + email + password</li>
          <li>Only emails you authorize can sign in; deactivate anytime</li>
        </ol>
      </div>

      {staff.length > 0 && (
        <div className="workspace-table-wrap">
          <div className="workspace-table-header">
            <h3>Team members</h3>
            <Link to="/workspace/staff">Manage all →</Link>
          </div>
          <table className="workspace-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Login status</th>
              </tr>
            </thead>
            <tbody>
              {staff.slice(0, 6).map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>{m.email}</td>
                  <td>
                    <span
                      className={`workspace-badge ${
                        m.loginReady && m.isActive ? 'ok' : m.isActive ? 'pending' : 'off'
                      }`}
                    >
                      {m.role === 'company_admin'
                        ? 'Admin'
                        : m.loginReady
                          ? 'Authorized · can log in'
                          : m.isActive
                            ? 'Authorized · pending password'
                            : 'Deactivated'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default WorkspaceStaffPanel;
